import { useContext, useEffect } from "react";
import { LayoutContext } from "../context/LayoutContext.jsx";
import { SiteContentContext } from "../context/SiteContentContext.jsx";
import BackgroundMedia, { parseBackgroundMedia } from "./backgroundMedia.jsx";
import ContentText from "./contentText.jsx";
import SectionContent from "./sectionContent.jsx";

/**
 * The site's page header: a background video over a fallback image, both admin-managed through
 * one BACKGROUND_MEDIA content key, with the title and subtitle over a dark overlay.
 *
 * `title`/`subtitle` take content the page already has (a category's name and its description
 * come from the database, not from a content key); `titleKey`/`subtitleKey` are for the pages
 * whose copy is site content. Every public page uses this one component, so they all gain a
 * video background the moment an admin sets one.
 */
export default function PageHero({
  titleKey,
  titleFallback,
  title,
  subtitleKey,
  subtitleFallback = "",
  subtitle,
  backgroundKey,
  height = "min-h-[60vh]",
  alignment = "center",
  fallbackClassName = "bg-gray-100",
}) {
  const { getContent, contentLoaded } = useContext(SiteContentContext);
  const { setTransparentHeader } = useContext(LayoutContext);

  useEffect(() => {
    setTransparentHeader(true);
    return () => setTransparentHeader(false);
  }, [setTransparentHeader]);

  const bg = parseBackgroundMedia(getContent(backgroundKey));
  const alignClass =
    alignment === "bottom" ? "items-end" : "items-center justify-center text-center";

  return (
    <section className={`relative -mt-16 flex ${alignClass} overflow-hidden ${height}`}>
      {contentLoaded && (bg.image || bg.video) ? (
        <BackgroundMedia
          imageUrl={bg.image || ""}
          videoUrl={bg.video || ""}
          overlayColor={bg.overlay?.color || "#000000"}
          overlayOpacity={bg.overlay?.opacity ?? 0.55}
          className="absolute inset-0 h-full w-full object-cover"
        />
      ) : (
        // Shown until an admin sets a background. The title and subtitle on top are white, so
        // a page whose key may legitimately sit empty passes a dark `fallbackClassName` rather
        // than leave white text on the light default.
        <div className={`absolute inset-0 h-full w-full ${fallbackClassName}`} />
      )}
      <div className="relative z-10 w-full pb-16 pt-32">
        <SectionContent>
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-4 leading-tight">
            {title ?? <ContentText contentKey={titleKey} fallback={titleFallback} />}
          </h1>
          {(subtitle ?? subtitleKey) && (
            <p
              className="text-white/85 text-base md:text-lg leading-relaxed md:max-w-[60%] whitespace-pre-line"
              data-testid="page-hero-subtitle"
            >
              {subtitle ?? <ContentText contentKey={subtitleKey} fallback={subtitleFallback} />}
            </p>
          )}
        </SectionContent>
      </div>
    </section>
  );
}
